<template>
  <div>
    <!-- Appeals Table -->
    <div class="bg-white rounded-lg shadow overflow-hidden">
      <div class="overflow-x-auto">
        <table class="w-full">
          <thead class="bg-gray-50 border-b border-gray-200">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Appeal Serial
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Contact Person
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Blood Group
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Pints Needed
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Appeal Date
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Action
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr
              v-for="appeal in filteredAppeals"
              :key="appeal.id"
              class="hover:bg-gray-50 transition-colors"
            >
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {{ appeal.apealSerial }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {{ appeal.patientName }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span
                  :class="[
                    'px-3 py-1 rounded-full text-xs font-semibold',
                    getBloodGroupColor(appeal.bloodGroup)
                  ]"
                >
                  {{ appeal.bloodGroup }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {{ appeal.pintsNeeded }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {{ appeal.appealDate }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span
                  :class="[
                    'px-3 py-1 rounded-full text-xs font-semibold',
                    getStatusColor(appeal.status)
                  ]"
                >
                  {{ appeal.status }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <div v-if="appeal.status === 'Pending Approval'" class="flex gap-2">
                  <button
                    @click="approveAppeal(appeal.id)"
                    class="px-3 py-1.5 bg-green-600 text-white text-sm font-medium rounded hover:bg-green-700 transition-colors"
                  >
                    Approve
                  </button>
                  <button
                    @click="rejectAppeal(appeal.id)"
                    class="px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded hover:bg-red-700 transition-colors"
                  >
                    Reject
                  </button>
                </div>
                <div v-else-if="appeal.status === 'Appeal Ongoing'" class="flex gap-2">
                  <button
                    @click="setPendingCollection(appeal.id)"
                    class="px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 transition-colors"
                  >
                    Mark Available
                  </button>
                </div>
                <div v-else-if="appeal.status === 'Pending Collection'" class="flex gap-2">
                  <button
                    @click="markCollected(appeal.id)"
                    class="px-3 py-1.5 bg-purple-600 text-white text-sm font-medium rounded hover:bg-purple-700 transition-colors"
                  >
                    Mark Collected
                  </button>
                </div>
                <span v-else :class="['px-3 py-1 rounded-full text-xs font-semibold']">
                  {{ appeal.status }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="flex items-center justify-center gap-2 py-4 border-t border-gray-200">
        <button
          @click="goToPage(currentPage - 1)"
          :disabled="currentPage === 1"
          :class="[
            'px-3 py-1 rounded transition-colors',
            currentPage === 1
              ? 'text-gray-400 cursor-not-allowed'
              : 'text-gray-700 hover:bg-gray-100'
          ]"
        >
          Previous
        </button>

        <button
          v-for="page in visiblePages"
          :key="page"
          @click="goToPage(page)"
          :class="[
            'px-3 py-1 rounded transition-colors',
            currentPage === page
              ? 'bg-blue-500 text-white'
              : 'text-gray-700 hover:bg-gray-100'
          ]"
        >
          {{ page }}
        </button>

        <span v-if="totalPages > 7" class="px-2 text-gray-500">...</span>

        <button
          v-if="totalPages > 7"
          @click="goToPage(totalPages)"
          :class="[
            'px-3 py-1 rounded transition-colors',
            currentPage === totalPages
              ? 'bg-blue-500 text-white'
              : 'text-gray-700 hover:bg-gray-100'
          ]"
        >
          {{ totalPages }}
        </button>

        <button
          @click="goToPage(currentPage + 1)"
          :disabled="currentPage === totalPages"
          :class="[
            'px-3 py-1 rounded transition-colors',
            currentPage === totalPages
              ? 'text-gray-400 cursor-not-allowed'
              : 'text-gray-700 hover:bg-gray-100'
          ]"
        >
          Next
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from '@/services/api';

export default {
  name: 'AppealsTab',
  props: {
    searchQuery: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      currentPage: 1,
      totalPages: 1,
      stats: {
        allAppeals: 0,
        pendingAppeals: 0,
        collectedAppeals: 0
      },
      appeals: []
    };
  },
  mounted() {
    this.fetchAppeals();
  },
  computed: {
    filteredAppeals() {
      let filtered = this.appeals;

      // Filter by search query
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(
          appeal =>
            appeal.apealSerial.toLowerCase().includes(query) ||
            appeal.patientName.toLowerCase().includes(query) ||
            appeal.bloodGroup.toLowerCase().includes(query)
        );
      }

      return filtered;
    },
    visiblePages() {
      const pages = [];
      const start = Math.max(1, this.currentPage - 2);
      const end = Math.min(this.totalPages, this.currentPage + 2);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      return pages;
    }
  },
  methods: {
    async fetchAppeals() {
      try {
        const response = await apiClient.getFacilityAppeals();
        // Assuming the response structure matches; adjust if needed
        const { stats, blood_appeals, pagination } = response.data || response;
        this.stats = {
          allAppeals: stats?.total_appeals || 0,
          pendingAppeals: (stats?.available_appeals || 0) + (stats?.pending_collection_appeals || 0),
          collectedAppeals: stats?.collected_appeals || 0
        };
        this.appeals = (blood_appeals || []).map(item => ({
          id: item.blood_appeals_id,
          apealSerial: item.appeal_serial || 'Unknown',
          patientName: item.recipient_name || item.user_name,
          bloodGroup: item.blood_group_name || 'Unknown',
          pintsNeeded: item.blood_pints,
          appealDate: new Date(item.created_at).toLocaleDateString(),
          status: this.mapStatus(item.status)
        }));
        this.totalPages = pagination?.total_pages || 1;
        this.currentPage = pagination?.current_page || 1;
        this.$emit('update-stats', this.stats);
      } catch (error) {
        console.error('Failed to fetch appeals:', error);
      }
    },
    mapStatus(status) {
      const statusMap = {
        'pending_approval': 'Pending Approval',
        'appeal_ongoing': 'Appeal Ongoing',
        'rejected': 'Rejected',
        'available': 'Available',
        'pending_collection': 'Pending Collection',
        'collected': 'Collected',
        'approved': 'Approved'
      };
      return statusMap[status] || status;
    },
    getStatusColor(status) {
      const colors = {
        'Pending Approval': 'bg-yellow-100 text-yellow-800',
        'Appeal Ongoing': 'bg-blue-100 text-blue-800',
        'Rejected': 'bg-red-100 text-red-800',
        'Available': 'bg-green-100 text-green-800',
        'Pending Collection': 'bg-orange-100 text-orange-800',
        'Collected': 'bg-purple-100 text-purple-800',
        'Approved': 'bg-green-100 text-green-800'
      };
      return colors[status] || 'bg-gray-100 text-gray-800';
    },
    getBloodGroupColor(bloodGroup) {
      const colors = {
        'A+': 'bg-red-100 text-red-700',
        'A-': 'bg-red-100 text-red-700',
        'B+': 'bg-pink-100 text-pink-700',
        'B-': 'bg-pink-100 text-pink-700',
        'AB+': 'bg-purple-100 text-purple-700',
        'AB-': 'bg-purple-100 text-purple-700',
        'O+': 'bg-orange-100 text-orange-700',
        'O-': 'bg-red-100 text-red-700'
      };
      return colors[bloodGroup] || 'bg-gray-100 text-gray-700';
    },
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        this.fetchAppeals();
      }
    },
    async approveAppeal(appealId) {
      try {
        await apiClient.approveBloodAppeal(appealId);
        // Refresh data after approval
        this.fetchAppeals();
      } catch (error) {
        console.error('Failed to approve appeal:', error);
      }
    },
    async rejectAppeal(appealId) {
      try {
        await apiClient.rejectBloodAppeal(appealId);
        // Refresh data after rejection
        this.fetchAppeals();
      } catch (error) {
        console.error('Failed to reject appeal:', error);
      }
    },
    async setAvailable(appealId) {
      try {
        await apiClient.setBloodAppealAvailable(appealId);
        // Refresh data after setting available
        this.fetchAppeals();
      } catch (error) {
        console.error('Failed to set appeal available:', error);
      }
    },
    async setPendingCollection(appealId) {
      try {
        await apiClient.setBloodAppealPendingCollection(appealId);
        // Refresh data after setting pending collection
        this.fetchAppeals();
      } catch (error) {
        console.error('Failed to set appeal pending collection:', error);
      }
    },
    async markCollected(appealId) {
      try {
        await apiClient.setBloodAppealCollected(appealId);
        // Refresh data after marking collected
        this.fetchAppeals();
      } catch (error) {
        console.error('Failed to mark appeal collected:', error);
      }
    }
  }
};
</script>

<style scoped>
/* Additional custom styles if needed */
</style>